import React from 'react';
import {
    Button as AriaButton,
    type ButtonProps as AriaButtonProps,
    FieldError as AriaFieldError,
    Group,
    Input,
    Label,
    Text,
    type ValidationResult,
    composeRenderProps,
} from 'react-aria-components';
import { useTranslation } from 'react-i18next';

import { classnames } from '@/utils/classnames.ts';

import Slot from './Slot.tsx';

// Props every field component accepts; Form.Field injects isInvalid and errorMessage.
export interface FieldBaseProps {
    label?: React.ReactNode;
    helpText?: React.ReactNode;
    errorMessage?: string | ((validation: ValidationResult) => string);
    placeholder?: string;
    // Shown at the end of the label row, e.g. how certain a pre-filled value is.
    labelAccessory?: React.ReactNode;
    className?: string;
}

// Styling-only wrappers: each part is slotted onto the matching react-aria element.
const FieldRoot = ({ className, ...props }: { className?: string; children: React.ReactElement }) => (
    <Slot {...props} className={classnames('flex w-full min-w-0 flex-col gap-1.5', className)} />
);

const FieldOptionalNote = () => {
    const { t } = useTranslation();
    return <span className="font-normal text-text-subtle">{t('common.optional')}</span>;
};

interface FieldInputProps {
    className?: string;
    isInvalid?: boolean;
    // Shown, not edited (a value to copy): outlined instead of filled, and nothing changes on hover or focus.
    isReadOnly?: boolean;
    children: React.ReactElement;
}

const fieldInputClassName = (isInvalid = false, isReadOnly = false) =>
    classnames(
        'flex h-10 w-full min-w-0 items-center rounded-xl border px-3.5 text-base outline-none transition-colors placeholder:text-text-subtle',
        'data-[disabled]:opacity-50',
        isReadOnly
            ? 'cursor-default border-border bg-transparent text-text'
            : isInvalid
              ? 'border-negative bg-negative-surface/50'
              : 'border-transparent bg-surface-muted hovered:bg-surface-hover',
        // A select's trigger stays pressed while its list is open, like a menu's.
        !isReadOnly &&
            'data-[focused]:border-text-muted data-[focus-within]:border-text-muted focus:border-text-muted data-[pressed]:border-text-muted'
    );

const FieldInput = ({ className, isInvalid, isReadOnly, ...props }: FieldInputProps) => (
    <Slot {...props} className={classnames(fieldInputClassName(isInvalid, isReadOnly), className)} />
);

interface FieldInputGroupProps extends Pick<FieldInputProps, 'isInvalid'> {
    placeholder?: string;
    // The buttons after the text (Field.InputButton).
    children: React.ReactNode;
}

// An input with buttons inside it at its end, e.g. to show a password or to open the options.
const FieldInputGroup = ({ isInvalid, placeholder, children }: FieldInputGroupProps) => (
    <FieldInput isInvalid={isInvalid} className="pr-1.5">
        <Group>
            <Input
                placeholder={placeholder}
                className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-text-subtle"
            />
            {children}
        </Group>
    </FieldInput>
);

const FieldInputButton = ({ className, ...props }: AriaButtonProps) => (
    <AriaButton
        {...props}
        className={composeRenderProps(className, (className) =>
            classnames(
                'flex size-7 items-center justify-center rounded-lg text-text-muted outline-none hovered:bg-surface-hover data-[focus-visible]:ring-2 data-[focus-visible]:ring-primary',
                className
            )
        )}
    />
);

interface FieldHeaderProps extends Pick<FieldBaseProps, 'label' | 'labelAccessory'> {
    isRequired?: boolean;
}

// Label row shared by all fields: label, "optional" note and the accessory.
const FieldHeader = ({ label, labelAccessory, isRequired = false }: FieldHeaderProps) => {
    if (!label && !labelAccessory) return null;
    return (
        <Label className="flex items-center gap-2 text-sm font-medium text-text">
            {label}
            {label && !isRequired && <FieldOptionalNote />}
            {labelAccessory && <span className="ml-auto font-normal">{labelAccessory}</span>}
        </Label>
    );
};

const FieldFooter = ({ helpText, errorMessage }: Pick<FieldBaseProps, 'helpText' | 'errorMessage'>) => (
    <>
        {helpText && (
            <Text slot="description" className="text-sm text-text-muted">
                {helpText}
            </Text>
        )}
        <AriaFieldError className="text-sm text-negative">{errorMessage}</AriaFieldError>
    </>
);

export default Object.assign(FieldRoot, {
    Header: FieldHeader,
    Footer: FieldFooter,
    Input: FieldInput,
    InputGroup: FieldInputGroup,
    InputButton: FieldInputButton,
});
